import { client } from '@/api/client.gen';
import { isAuthStoreCreated, useAuthStore } from '@/stores/auth';
import { getFirebaseAuth } from '@/config/firebase';
import { ApiError, NetworkError } from '@/utils/errorHandler';

export function configureApi(): void {
  // Configure the client with base URL and auth
  client.setConfig({
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- an empty/falsy value must use the fallback
    baseUrl: import.meta.env.VITE_API_URL || '',
    throwOnError: true,
    auth: async () => {
      // Public pages (e.g. /external/...) never instantiate the auth store;
      // instantiating it here would initialize Firebase, so just send no token.
      if (!isAuthStoreCreated()) return undefined;
      // Fall back to the SDK's currentUser: right after account creation the
      // store's user ref is only set once the async onAuthStateChanged
      // handler runs, while auth.currentUser is available immediately. Store
      // creation implies Firebase is booted, so this never triggers an init.
      const user = useAuthStore().user ?? getFirebaseAuth().currentUser;
      if (!user) throw new Error('User not authenticated');
      return user.getIdToken();
    },
  });

  // The browser's bare "TypeError: Failed to fetch" says nothing about which
  // call failed or why. No response means the request never got an HTTP
  // answer, so name the endpoint and the client state that usually explains it.
  // An HTTP error otherwise arrives as the bare parsed body, losing the status;
  // views need it to tell "not available" (401) from a real failure.
  client.interceptors.error.use((error, response, request, options) => {
    if (response !== undefined) {
      return response.ok ? error : new ApiError(response.status, error);
    }
    if (request === undefined || !(error instanceof TypeError)) {
      return error;
    }
    return new NetworkError(`${request.method} ${options.url}`, new URL(request.url).host, error);
  });
}
